import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { checkout_form } from "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";
import { useCheckoutFormContext } from "repoV2/features/CheckoutPages/modules/CheckoutFormContextProvider";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import {
    getCountryDataBasedOnTimezone,
    getUserTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { getCountryIsIndia } from "repoV2/utils/common/location/location";
import { IGSTDetails } from "repoV2/features/Common/modules/FormData/interfaces/GSTDetailsFormGroup.interfaces";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { GST_STATUS } from "repoV2/features/Listing/Listing.constants";
import { indian_state_gstin_to_state } from "repoV2/features/Common/modules/Location/constants/indianStatesGstData";
import { getFormattedIndianStates } from "repoV2/features/Common/modules/Location/utils/getFormattedIndianStates";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import { checkIsValidIndianPincode } from "repoV2/features/Common/modules/Location/utils/pincode";

const {
    gst_details: { field_names },
    user_details: {
        field_names: { billing_state },
    },
} = checkout_form;

const { indian_states_and_uts_dropdown_map } = getFormattedIndianStates();

export const useGSTDetailsFormGroup = () => {
    const { listingUuid, eInvoiceEnabled } = useCheckoutBasicDetailsContext();
    const { getValues, setValue, clearErrors, setError, unregister } =
        useFormContext();
    const { setFormData } = useSetFormData();
    const { isGstEnabled } = useCheckoutFormContext();

    // @dev
    // This value is directly picked and manipulated from reducer on user interaction
    // why:
    //      - we need to re-render whenever this is changed
    //      - In case of mobile, we show the form as a modal. To
    //          persist the value in this case.
    const isGstActive = useGetFormData<IGSTDetails["active"]>({
        key: `${FORM_KEYS.gst_details}.active`,
    });

    const userDetails =
        useGetFormData<{ [userId: string]: IUserDetails }>({
            key: `${FORM_KEYS.user_details}`,
        }) || {};

    const priceDetails = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.priceSection?.data
    );

    const gstState = priceDetails?.gst_state;

    const setGSTDetailsFormData = ({
        value,
        fieldName,
    }: {
        fieldName: string;
        value?: string;
    }) => {
        const data = getValues();
        let fieldValue: string = data[fieldName];
        if (fieldName === field_names.registration_number)
            fieldValue = fieldValue.toUpperCase();
        setFormData(
            `${FORM_KEYS.gst_details}.${fieldName}`,
            value || fieldValue
        );
    };

    const setGSTINDetailsFormData = () => {
        const data = getValues() || {};
        let fieldValue: string = data[field_names.registration_number];
        fieldValue = fieldValue.toUpperCase();

        if (fieldValue.length > 2) {
            // If GSTIN has more than 2 characters, then we check the first
            // two characters if they map to a valid state
            const state_code: string = fieldValue.substring(0, 2);
            const stateBasedOnGSTIN = indian_state_gstin_to_state?.[state_code];
            if (stateBasedOnGSTIN) {
                // If we get valid statecode in GSTIN (based on first 2 characters), we should clear errors
                clearErrors(field_names.registration_number);

                // If we have a state for the GSTIN, then we find all the valid
                // billing_states that are in the form currently
                const allBillingStateFormKeys = Object.keys(data).filter(key =>
                    key.includes(billing_state)
                );

                // We'll set the value of this state for all the billing states
                // There will be more than one billing_state in case of multiple users
                // TODO: Handle this for all the users using 'allUsersIds' state
                // in multiple users
                allBillingStateFormKeys.forEach(key => {
                    setValue(
                        key,
                        indian_states_and_uts_dropdown_map[stateBasedOnGSTIN]
                    );
                });

                const newUserDetails: {
                    [userId: string]: IUserDetails;
                } = {};

                // For all the users change this billing_state
                // we take GST details from only 1 user and consider
                // the same for rest of the users
                Object.keys(userDetails).forEach(userId => {
                    newUserDetails[userId] = {
                        ...userDetails[userId],
                        billing_state:
                            indian_states_and_uts_dropdown_map[
                                stateBasedOnGSTIN
                            ],
                    };
                });

                setFormData(`${FORM_KEYS.user_details}`, newUserDetails);
            } else {
                setError(field_names.registration_number, {
                    type: "custom",
                    message: "Invalid GST Number",
                });
            }
        }

        setFormData(
            `${FORM_KEYS.gst_details}.${field_names.registration_number}`,
            fieldValue
        );
    };

    const handleGSTChecked = (value: boolean) => {
        // Incase customer unchecks the 'GSTChecked check', the we need to clear the
        // 'company name' and 'GST Number' fields
        if (!value) {
            // removing the 'company name' and 'GST Number' fields from the form
            unregister([
                field_names.company_name,
                field_names.registration_number,
            ]);

            // removing the same from redux as well
            setFormData(`${FORM_KEYS.gst_details}`, {
                [field_names.active]: value,
            });
        }
        setValue(field_names.active, value);
        setFormData(`${FORM_KEYS.gst_details}.${field_names.active}`, value);
    };

    const isGstRequired = getIsGstRequired({
        isGstEnabled: Boolean(isGstEnabled),
        gstState,
    });

    return {
        isExpanded: Boolean(isGstActive),
        isGstRequired,
        handleGSTChecked,
        setGSTDetailsFormData,
        setGSTINDetailsFormData,
        eInvoiceEnabled,
    };
};

export const getIsGstRequired = ({
    isGstEnabled,
    gstState,
}: {
    isGstEnabled: boolean;
    gstState?: number;
}) => {
    const timezone = getUserTimezone();
    const country = getCountryDataBasedOnTimezone({ timezone });
    const isIndia = getCountryIsIndia(country?.name);

    // We show gst fields only if gst is enabled, user is domestic,
    // an gst state is 'not inapplicable'.
    // We don;t just check for 'gstState === GST_STATUS.INAPPLICABLE' for the
    // case where gstState might come undefined or null
    const isGSTStateNotInapplicable =
        gstState === GST_STATUS.INCLUSIVE || gstState === GST_STATUS.EXCLUSIVE;

    return isGSTStateNotInapplicable && isGstEnabled && isIndia;
};

export const validators = {
    [field_names.company_name]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value)
            ? "Please enter Registered Company Name"
            : true;
    },
    [field_names.registration_number]: (value: string) => {
        const _value = value.trim();
        if (checkIsStringEmpty(_value)) {
            return "Please enter a valid GST Number";
        }
        if (_value.length > 2) {
            const state_code: string = _value.substring(0, 2);
            const stateBasedOnGSTIN = indian_state_gstin_to_state?.[state_code];
            return stateBasedOnGSTIN ? true : "Invalid GST Number";
        }
        return true;
    },
    [field_names.company_address]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value)
            ? "Please enter Registered Company Address"
            : true;
    },
    [field_names.company_pincode]: (value: string) => {
        const _value = value.trim();
        if (checkIsStringEmpty(_value)) {
            return "Please enter a valid Pincode";
        }
        if (!checkIsValidIndianPincode(_value)) {
            return "Invalid Pincode";
        }
        return true;
    },
};
